import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Tab, IconButton, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SchoolIcon from '@mui/icons-material/School';
import hljs from 'highlight.js';
import 'highlight.js/styles/atom-one-dark.css'; // Choose a style
import CodeTester from './CodeTester';
import MermaidDiagram from '../common/MermaidDiagram';
import { decodeHTMLEntities } from '../../utils/stringUtils';

const openCodeInVSCode = async (codeSnippet: string, language: string = 'javascript') => {
    const AUTH_TOKEN = "jIVxNFGzeImEYxK2vjMKCP2m349bqthuDTagDZadqZ87qLwozmaXjUn";

    try {
        const response = await fetch('http://127.0.0.1:4842/open', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${AUTH_TOKEN}`
            },
            body: JSON.stringify({
                content: codeSnippet,
                language: language
            })
        });

        if (!response.ok) {
            throw new Error("Erreur d'authentification avec l'extension");
        }
    } catch (error) {
        console.error(error);
        alert("Impossible de communiquer avec Edu-JALM Helper.");
    }
};


interface BlockRendererProps {
    block: any;
}

interface TabItem {
    title: string;
    content: string;
}

// Separate component for Tabs to manage state
const TabsBlock: React.FC<{ tabs: TabItem[] }> = ({ tabs }) => {
    const [activeTab, setActiveTab] = useState(0);

    if (!tabs || tabs.length === 0) return null;

    return (
        <Box sx={{ mb: 3 }}>
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid #e0e0e0'
                }}
            >
                <Tabs
                    value={activeTab}
                    onChange={(_, newValue) => setActiveTab(newValue)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        bgcolor: '#f5f5f5',
                        borderBottom: '1px solid #e0e0e0',
                        minHeight: 48,
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.95rem',
                            minHeight: 48,
                            px: 3,
                            '&.Mui-selected': {
                                bgcolor: '#fff',
                                color: '#1976d2',
                            }
                        },
                        '& .MuiTabs-indicator': {
                            height: 3,
                            borderRadius: '3px 3px 0 0'
                        }
                    }}
                >
                    {tabs.map((tab, index) => (
                        <Tab key={index} label={decodeHTMLEntities(tab.title)} />
                    ))}
                </Tabs>
                <Box
                    sx={{
                        p: 3,
                        bgcolor: '#fff',
                        '& b, & strong': { fontWeight: 'bold' },
                        '& i, & em': { fontStyle: 'italic' },
                        '& p': { mb: 1.5, lineHeight: 1.7 },
                        '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1, display: 'block', mx: 'auto' }
                    }}
                    dangerouslySetInnerHTML={{ __html: tabs[activeTab]?.content || '' }}
                />
            </Paper>
        </Box>
    );
};

const BlockRenderer: React.FC<BlockRendererProps> = ({ block }) => {
    useEffect(() => {
        hljs.highlightAll();
    }, [block]);

    switch (block.type) {
        case 'text':
            return (
                <Box
                    sx={{
                        mb: 2,
                        '& p': { mb: 1.5, lineHeight: 1.7, fontSize: '1rem', color: '#333' },
                        '& h1, & h2, & h3': { fontWeight: 700, mt: 3, mb: 2, color: '#2c3e50' },
                        '& ul, & ol': { pl: 3, mb: 2 },
                        '& li': { mb: 0.5 },
                        '& a': { color: '#1976d2', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
                        '& blockquote': { borderLeft: '4px solid #ccc', pl: 2, py: 1, my: 2, bgcolor: '#f9f9f9', fontStyle: 'italic' },
                        // Style for embedded images in HTML content
                        '& img': {
                            maxWidth: '100%',
                            height: 'auto',
                            objectFit: 'contain',
                            display: 'block',
                            mx: 'auto',
                            borderRadius: 1
                        },
                        '& figure': {
                            m: 0,
                            mb: 2,
                            textAlign: 'center'
                        },
                        // Hide images with empty src
                        '& img[src=""], & img:not([src])': {
                            display: 'none'
                        }
                    }}
                    dangerouslySetInnerHTML={{ __html: block.content || '' }}
                />
            );

        case 'code':
            const codeContent = typeof block.content === 'string'
                ? { code: block.content, language: 'javascript' }
                : block.content || { code: '', language: 'javascript' };

            return (
                <Box sx={{ mb: 3, borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                    <Box sx={{ bgcolor: '#282c34', px: 2, py: 1, borderBottom: '1px solid #3e4451', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ color: '#abb2bf', fontFamily: 'monospace', textTransform: 'uppercase' }}>
                            {codeContent.language}
                        </Typography>
                        <Tooltip title="Ouvrir avec Visual Studio Code" arrow>
                            <IconButton
                                size="small"
                                onClick={() => openCodeInVSCode(codeContent.code, codeContent.language)}
                                sx={{
                                    color: '#abb2bf',
                                    '&:hover': {
                                        color: '#61dafb',
                                        bgcolor: 'rgba(97, 218, 251, 0.1)'
                                    },
                                    transition: 'all 0.2s'
                                }}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M17.583 2.248L12.23 6.6l-4.088-3.104L2.5 5.81v12.38l5.643 2.314L12.23 17.4l5.353 4.352L23.5 19.065V4.935l-5.917-2.687zM8.143 16.676V7.324l9.714 4.676-9.714 4.676z" />
                                </svg>
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <pre style={{ margin: 0 }}>
                        <code className={`language-${codeContent.language}`} style={{ padding: '20px', display: 'block', fontSize: '0.9rem', lineHeight: 1.5 }}>
                            {codeContent.code}
                        </code>
                    </pre>
                </Box>
            );

        case 'heading':
            const hContent = typeof block.content === 'object' ? block.content : { text: block.content || '', level: 2 };
            // Ensure level is between 1 and 6
            const hLevel = Math.max(1, Math.min(6, hContent.level || 2));
            const variant = `h${hLevel}` as any;

            return (
                <Typography
                    variant={variant}
                    sx={{
                        fontWeight: 700,
                        mt: 4,
                        mb: 2,
                        color: '#2c3e50',
                        fontSize: hLevel === 1 ? '2.25rem' : hLevel === 2 ? '1.8rem' : hLevel === 3 ? '1.5rem' : hLevel === 4 ? '1.25rem' : '1.1rem'
                    }}
                >
                    {decodeHTMLEntities(hContent.text)}
                </Typography>
            );

        case 'list':
            return (
                <Box
                    sx={{
                        mb: 3,
                        pl: 2,
                        '& ul, & ol': { pl: 3, mb: 0 },
                        '& li': { mb: 1, lineHeight: 1.6, color: '#333' }
                    }}
                    dangerouslySetInnerHTML={{
                        __html: block.content?.includes('<li') ? block.content : `<ul><li>${block.content?.split('\n').join('</li><li>')}</li></ul>`
                    }}
                />
            );

        case 'image':
            // Handle both 'url' and 'src' properties (import uses src, manual uses url)
            const imgContent = typeof block.content === 'string'
                ? { url: block.content, alt: '', description: '' }
                : (block.content || {});
            const imageUrl = imgContent.url || imgContent.src || '';

            // Don't render if no valid URL or blob URL
            if (!imageUrl || imageUrl.trim() === '' || imageUrl.startsWith('blob:') || imageUrl === 'undefined') {
                console.warn('Skipping invalid image URL:', imageUrl || '(empty)');
                return null;
            }

            return (
                <Box component="figure" sx={{ m: 0, mb: 3, textAlign: 'center' }}>
                    <Box
                        component="img"
                        src={imageUrl}
                        alt={imgContent.alt || ''}
                        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                            console.warn('Image failed to load:', imageUrl);
                            e.currentTarget.style.display = 'none';
                        }}
                        sx={{
                            maxWidth: '100%',
                            width: 'auto',
                            height: 'auto',
                            objectFit: 'contain',
                            borderRadius: 2,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                            display: 'block',
                            mx: 'auto'
                        }}
                    />
                    {(imgContent.description || imgContent.alt) && (
                        <Typography component="figcaption" variant="caption" sx={{ display: 'block', mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                            {imgContent.description || imgContent.alt}
                        </Typography>
                    )}
                </Box>
            );

        case 'video':
            const vidContent = typeof block.content === 'string' ? { url: block.content, alt: '', description: '', player: 'youtube' } : block.content;

            const getEmbedUrl = (url: string) => {
                if (!url) return '';
                if (url.includes('youtube.com') || url.includes('youtu.be')) {
                    const videoId = url.split('v=')[1] || url.split('/').pop();
                    const cleanId = videoId?.split('&')[0];
                    return `https://www.youtube.com/embed/${cleanId}`;
                }
                return url;
            };

            return (
                <Box component="figure" sx={{ m: 0, mb: 3 }}>
                    <Box sx={{ position: 'relative', paddingTop: '56.25%', borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
                        {vidContent.player === 'youtube' ? (
                            <iframe
                                src={getEmbedUrl(vidContent.url)}
                                title={vidContent.alt || 'Video player'}
                                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        ) : (
                            <video
                                src={vidContent.url}
                                controls
                                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                            />
                        )}
                    </Box>
                    {vidContent.description && (
                        <Typography component="figcaption" variant="caption" sx={{ display: 'block', mt: 1, fontStyle: 'italic', color: 'text.secondary', textAlign: 'center' }}>
                            {vidContent.description}
                        </Typography>
                    )}
                </Box>
            );

        case 'info':
        case 'warning':
        case 'danger':
        case 'success':
        case 'definition':
            const getAlertConfig = (type: string) => {
                switch (type) {
                    case 'info': return { color: '#0288d1', bgcolor: '#e1f5fe', icon: <InfoIcon />, title: 'Information' };
                    case 'warning': return { color: '#ed6c02', bgcolor: '#fff3e0', icon: <WarningIcon />, title: 'Attention' };
                    case 'danger': return { color: '#d32f2f', bgcolor: '#ffebee', icon: <ErrorIcon />, title: 'Important' };
                    case 'success': return { color: '#2e7d32', bgcolor: '#e8f5e9', icon: <CheckCircleIcon />, title: 'Astuce' };
                    case 'definition': return { color: '#9c27b0', bgcolor: '#f3e5f5', icon: <SchoolIcon />, title: 'Définition' };
                    default: return { color: '#0288d1', bgcolor: '#e1f5fe', icon: <InfoIcon />, title: 'Note' };
                }
            };
            const config = getAlertConfig(block.type);
            return (
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        mb: 3,
                        bgcolor: config.bgcolor,
                        borderLeft: `4px solid ${config.color}`,
                        borderRadius: '0 4px 4px 0',
                        display: 'flex',
                        gap: 2
                    }}
                >
                    <Box sx={{ color: config.color, mt: 0.5 }}>{config.icon}</Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: config.color, mb: 0.5 }}>
                            {config.title}
                        </Typography>
                        <Box
                            sx={{
                                color: '#333',
                                '& b, & strong': { fontWeight: 'bold' },
                                '& i, & em': { fontStyle: 'italic' },
                                '& u': { textDecoration: 'underline' },
                                '& p': { m: 0 },
                                '& a': { color: config.color, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }
                            }}
                            dangerouslySetInnerHTML={{ __html: block.content || '' }}
                        />
                    </Box>
                </Paper>
            );

        case 'table':
            const tableData = block.content || { headers: [], rows: [] };
            return (
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, borderRadius: 2, overflow: 'hidden', maxWidth: '100%', overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                {tableData.headers?.map((header: string, i: number) => (
                                    <TableCell key={i} sx={{ fontWeight: 'bold', color: '#555' }}>{header}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tableData.rows?.map((row: string[], i: number) => (
                                <TableRow key={i} hover>
                                    {row.map((cell: string, j: number) => (
                                        <TableCell key={j}>{cell}</TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            );

        case 'html':
            return (
                <Box sx={{ mb: 3 }} dangerouslySetInnerHTML={{ __html: block.content || '' }} />
            );

        case 'codeTester':
            const testerContent = block.content || { baseCode: '', language: 'html' };
            return (
                <Box sx={{ mb: 3 }}>
                    <CodeTester
                        initialCode={testerContent.baseCode}
                        language={testerContent.language}
                        mode="learner"
                    />
                </Box>
            );

        case 'tabs':
            return <TabsBlock tabs={Array.isArray(block.content) ? block.content : (block.content?.tabs || [])} />;

        case 'mermaid':
            return (
                <Box sx={{ mb: 3 }}>
                    <MermaidDiagram chart={block.content || ''} />
                </Box>
            );

        default:
            return null;
    }
};

export default BlockRenderer;
